import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ModalWrapper } from '../../../components/ModalWrapper';
import styled from 'styled-components';
import { Button, CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoon,
  faTrash,
  faSearchPlus,
  faSearchMinus,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faPaintBrush,
  faEraser,
} from '@fortawesome/free-solid-svg-icons';
import { DarkMode, BG_COLOR } from '../types/darkMode';

const BRUSH_SIZES = [1, 2, 5, 10, 20, 40] as const;
const ZOOM_LEVELS = [1, 2, 4] as const;

interface IProps {
  enhancedImageUrl: string;
  originalImageUrl: string;
  itemId: number;
  alternativeNumber: 1 | 2;
  onClose: () => void;
  onSubmit: (
    itemId: number,
    alternativeNumber: number,
    maskBase64: string,
    deltaX: number,
    deltaY: number,
  ) => Promise<void>;
}

export const FaceFixModal: React.FC<IProps> = ({
  enhancedImageUrl,
  originalImageUrl,
  itemId,
  alternativeNumber,
  onClose,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(DarkMode.LIGHT);
  const [zoom, setZoom] = useState(2);
  const [delta, setDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [brushSize, setBrushSize] = useState(10);
  const [isErasing, setIsErasing] = useState(false);
  const [hasMask, setHasMask] = useState(false);

  const enhancedImgRef = useRef<HTMLImageElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const isPaintingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const toggleDarkMode = () => {
    setDarkMode((d) => (d === DarkMode.LIGHT ? DarkMode.DARK : DarkMode.LIGHT));
  };

  const zoomIn = () => {
    setZoom((z) => {
      const idx = ZOOM_LEVELS.indexOf(z as any);
      return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : z;
    });
  };

  const zoomOut = () => {
    setZoom((z) => {
      const idx = ZOOM_LEVELS.indexOf(z as any);
      return idx > 0 ? ZOOM_LEVELS[idx - 1] : z;
    });
  };

  const handleImageLoad = useCallback(() => {
    if (enhancedImgRef.current) {
      const w = enhancedImgRef.current.naturalWidth;
      const h = enhancedImgRef.current.naturalHeight;
      setImageNaturalSize({ width: w, height: h });

      // Initialize mask canvas at natural resolution
      if (maskCanvasRef.current) {
        maskCanvasRef.current.width = w;
        maskCanvasRef.current.height = h;
        const ctx = maskCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.fillStyle = 'black';
          ctx.fillRect(0, 0, w, h);
        }
      }
    }
  }, []);

  // Convert display coordinates to image pixel coordinates
  const displayToImageCoords = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      if (!enhancedImgRef.current || !imageNaturalSize) return null;

      const imgRect = enhancedImgRef.current.getBoundingClientRect();
      const scaleX = imageNaturalSize.width / imgRect.width;
      const scaleY = imageNaturalSize.height / imgRect.height;

      return {
        x: (clientX - imgRect.left) * scaleX,
        y: (clientY - imgRect.top) * scaleY,
      };
    },
    [imageNaturalSize],
  );

  // Draw a brush stroke on the mask canvas between two points
  const paintOnMask = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }, erase: boolean) => {
      const maskCtx = maskCanvasRef.current?.getContext('2d');
      if (!maskCtx) return;

      maskCtx.globalCompositeOperation = 'source-over';
      maskCtx.fillStyle = erase ? 'black' : 'white';
      maskCtx.strokeStyle = erase ? 'black' : 'white';
      maskCtx.lineWidth = brushSize;
      maskCtx.lineCap = 'round';
      maskCtx.lineJoin = 'round';

      // Draw line between points
      maskCtx.beginPath();
      maskCtx.moveTo(from.x, from.y);
      maskCtx.lineTo(to.x, to.y);
      maskCtx.stroke();

      // Draw circle at endpoint for single clicks
      maskCtx.beginPath();
      maskCtx.arc(to.x, to.y, brushSize / 2, 0, Math.PI * 2);
      maskCtx.fill();
    },
    [brushSize],
  );

  // Update the visible overlay canvas from the mask
  const updateOverlay = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    if (!maskCanvas || !overlayCanvas || !imageNaturalSize) return;

    overlayCanvas.width = imageNaturalSize.width;
    overlayCanvas.height = imageNaturalSize.height;

    const maskCtx = maskCanvas.getContext('2d');
    const overlayCtx = overlayCanvas.getContext('2d');
    if (!maskCtx || !overlayCtx) return;

    const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    const overlayImageData = overlayCtx.createImageData(maskCanvas.width, maskCanvas.height);

    let hasAnyWhite = false;
    for (let i = 0; i < maskData.data.length; i += 4) {
      const isWhite = maskData.data[i] > 128;
      if (isWhite) {
        hasAnyWhite = true;
        // Purple tint: rgba(156, 39, 176, 0.3)
        overlayImageData.data[i] = 156;
        overlayImageData.data[i + 1] = 39;
        overlayImageData.data[i + 2] = 176;
        overlayImageData.data[i + 3] = 77; // ~0.3 opacity
      }
      // else stays transparent (0,0,0,0)
    }

    overlayCtx.putImageData(overlayImageData, 0, 0);
    setHasMask(hasAnyWhite);
  }, [imageNaturalSize]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isLoading) return;

      // Ignore clicks on scrollbars: check if click is outside the client area
      const container = e.currentTarget as HTMLElement;
      const rect = container.getBoundingClientRect();
      if (
        e.clientX > rect.left + container.clientWidth ||
        e.clientY > rect.top + container.clientHeight
      ) {
        return;
      }

      e.preventDefault();

      const coords = displayToImageCoords(e.clientX, e.clientY);
      if (!coords) return;

      const erase = e.altKey || isErasing;
      isPaintingRef.current = true;
      lastPointRef.current = coords;

      // Paint single dot
      paintOnMask(coords, coords, erase);
      updateOverlay();
    },
    [isLoading, displayToImageCoords, paintOnMask, updateOverlay, isErasing],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPaintingRef.current || !lastPointRef.current) return;
      e.preventDefault();

      const coords = displayToImageCoords(e.clientX, e.clientY);
      if (!coords) return;

      const erase = e.altKey || isErasing;
      paintOnMask(lastPointRef.current, coords, erase);
      lastPointRef.current = coords;
      updateOverlay();
    },
    [displayToImageCoords, paintOnMask, updateOverlay, isErasing],
  );

  const handleMouseUp = useCallback(() => {
    isPaintingRef.current = false;
    lastPointRef.current = null;
  }, []);

  // Handle mouse up outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isPaintingRef.current = false;
      lastPointRef.current = null;
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  // Handle keyboard arrow keys for delta adjustment
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setDelta((d) => ({ ...d, x: d.x - 1 }));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setDelta((d) => ({ ...d, x: d.x + 1 }));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setDelta((d) => ({ ...d, y: d.y - 1 }));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setDelta((d) => ({ ...d, y: d.y + 1 }));
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading]);

  const handleClear = () => {
    if (!maskCanvasRef.current || !imageNaturalSize) return;
    const ctx = maskCanvasRef.current.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, imageNaturalSize.width, imageNaturalSize.height);
    }
    updateOverlay();
  };

  const handleFix = async () => {
    if (!maskCanvasRef.current || !hasMask) return;

    // Export mask as base64 PNG
    const dataUrl = maskCanvasRef.current.toDataURL('image/png');
    const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');

    setIsLoading(true);
    try {
      await onSubmit(itemId, alternativeNumber, base64, delta.x, delta.y);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to fix faces');
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper
      onClose={onClose}
      title={`Fix Faces - Alternative ${alternativeNumber}`}
      width={1100}
      height={850}
      disableBackdropClick={isLoading}
    >
      <ContentWrapper>
        <ToolbarRow>
          <ToolbarButtons>
            <SmallButton onClick={zoomOut} disabled={zoom === ZOOM_LEVELS[0]} title="Zoom out">
              <FontAwesomeIcon icon={faSearchMinus} />
            </SmallButton>
            <ZoomLabel>{zoom}x</ZoomLabel>
            <SmallButton onClick={zoomIn} disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]} title="Zoom in">
              <FontAwesomeIcon icon={faSearchPlus} />
            </SmallButton>
            <ToolbarSeparator />
            <SmallButton onClick={() => setDelta((d) => ({ ...d, x: d.x - 1 }))} title="Move original left">
              <FontAwesomeIcon icon={faArrowLeft} />
            </SmallButton>
            <SmallButton onClick={() => setDelta((d) => ({ ...d, y: d.y - 1 }))} title="Move original up">
              <FontAwesomeIcon icon={faArrowUp} />
            </SmallButton>
            <SmallButton onClick={() => setDelta((d) => ({ ...d, y: d.y + 1 }))} title="Move original down">
              <FontAwesomeIcon icon={faArrowDown} />
            </SmallButton>
            <SmallButton onClick={() => setDelta((d) => ({ ...d, x: d.x + 1 }))} title="Move original right">
              <FontAwesomeIcon icon={faArrowRight} />
            </SmallButton>
            <DeltaLabel>({delta.x}, {delta.y})</DeltaLabel>
            <ToolbarSeparator />
            <ModeButton $active={!isErasing} onClick={() => setIsErasing(false)} title="Paint (add to mask)">
              <FontAwesomeIcon icon={faPaintBrush} />
            </ModeButton>
            <ModeButton $active={isErasing} onClick={() => setIsErasing(true)} title="Erase (remove from mask)">
              <FontAwesomeIcon icon={faEraser} />
            </ModeButton>
            <ToolbarSeparator />
            {BRUSH_SIZES.map((size) => (
              <BrushSizeButton
                key={size}
                $active={brushSize === size}
                onClick={() => setBrushSize(size)}
                title={`Brush size ${size}px`}
              >
                <BrushDot $size={Math.max(4, Math.min(size / 2, 16))} />
              </BrushSizeButton>
            ))}
            <ToolbarSeparator />
            <SmallButton onClick={handleClear} disabled={!hasMask || isLoading} title="Clear mask">
              <FontAwesomeIcon icon={faTrash} />
            </SmallButton>
          </ToolbarButtons>
          <ToolbarButtons>
            <HintText>Option = erase</HintText>
            <SmallButton onClick={toggleDarkMode} title="Toggle background">
              <FontAwesomeIcon icon={faMoon} />
            </SmallButton>
          </ToolbarButtons>
        </ToolbarRow>

        <ScrollContainer
          $bgColor={BG_COLOR[darkMode]}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <InnerWrapper>
            <StyledImage
              ref={enhancedImgRef}
              src={enhancedImageUrl}
              alt="Enhanced"
              onLoad={handleImageLoad}
              draggable={false}
              style={
                imageNaturalSize
                  ? { width: imageNaturalSize.width * zoom, height: imageNaturalSize.height * zoom }
                  : undefined
              }
            />
            {/* Original image overlay for alignment */}
            {imageNaturalSize && (
              <OriginalOverlay
                src={originalImageUrl}
                alt="Original overlay"
                draggable={false}
                style={{
                  width: imageNaturalSize.width * zoom,
                  height: imageNaturalSize.height * zoom,
                  transform: `translate(${delta.x * zoom}px, ${delta.y * zoom}px)`,
                }}
              />
            )}
            {/* Mask overlay canvas */}
            {imageNaturalSize && (
              <OverlayCanvas
                ref={overlayCanvasRef}
                style={{
                  width: imageNaturalSize.width * zoom,
                  height: imageNaturalSize.height * zoom,
                }}
              />
            )}
          </InnerWrapper>
        </ScrollContainer>
        {/* Hidden mask canvas at natural resolution */}
        <canvas ref={maskCanvasRef} style={{ display: 'none' }} />

        <ButtonSection>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFix}
            disabled={isLoading || !hasMask}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Fixing...' : 'Fix'}
          </Button>
        </ButtonSection>
      </ContentWrapper>
    </ModalWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToolbarButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ToolbarSeparator = styled.div`
  width: 1px;
  height: 20px;
  background-color: #e0e0e0;
  margin: 0 4px;
`;

const ZoomLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  min-width: 24px;
  text-align: center;
`;

const SmallButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  color: #607d8b;
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: #455a64;
    background-color: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const ModeButton = styled.button<{ $active: boolean }>`
  padding: 4px 8px;
  background: ${(p) => (p.$active ? '#e8eaf6' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#7c4dff' : '#e0e0e0')};
  border-radius: 4px;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#7c4dff' : '#607d8b')};
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    color: #7c4dff;
    background-color: #e8eaf6;
  }
`;

const BrushSizeButton = styled.button<{ $active: boolean }>`
  width: 28px;
  height: 28px;
  background: ${(p) => (p.$active ? '#e8eaf6' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#7c4dff' : '#e0e0e0')};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7c4dff;
    background-color: #e8eaf6;
  }
`;

const BrushDot = styled.div<{ $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background-color: #607d8b;
`;

const HintText = styled.span`
  font-size: 11px;
  color: #aaa;
  font-style: italic;
`;

const ScrollContainer = styled.div<{ $bgColor: string }>`
  width: 100%;
  flex: 1;
  background-color: ${(props) => props.$bgColor};
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  overflow: scroll;
  cursor: crosshair;
  user-select: none;
  transition: background-color 0.2s ease;

  &::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }
  &::-webkit-scrollbar-thumb {
    background: #bbb;
    border-radius: 7px;
    border: 3px solid #f0f0f0;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`;

const InnerWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledImage = styled.img`
  display: block;
`;

const OriginalOverlay = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  opacity: 0.5;
  pointer-events: none;
`;

const OverlayCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;

const DeltaLabel = styled.span`
  font-size: 11px;
  color: #888;
  min-width: 50px;
  text-align: center;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
`;
